<script setup lang="ts">
import type { EnrolmentComparisonsTable } from '~/data/enrolmentComparisons'

defineProps({
  comparisons: { type: Object as PropType<EnrolmentComparisonsTable>, required: true }
})

defineEmits(['scrollToSelfEnrol', 'scrollToProgrammeEnrol'])
</script>

<template>
  <div id="enrol-table">
    <div class="table-header table-row">
      <div class="table-header-cell"/>
      <div class="table-header-cell">
        <h1>{{ comparisons.selfTitle }}</h1>
        <CommonButton arrow @handle-click="$emit('scrollToSelfEnrol')">
          Start applying
        </CommonButton>
      </div>
      <div class="table-header-cell">
        <h1>{{ comparisons.programmeTitle }}</h1>
        <CommonButton arrow @handle-click="$emit('scrollToProgrammeEnrol')">
          Start applying
        </CommonButton>
      </div>
    </div>

    <div v-for="compare in comparisons.comparisons" :key="compare.title" class="table-row">
      <div class="table-title table-cell">
        <h4>
          {{ compare.title }}
        </h4>
      </div>

      <div class="table-cell" v-html="compare.self"/>

      <div class="table-cell" v-html="compare.programme"/>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$border: 1px solid gainsboro;

#enrol-table {
  position: relative;
  margin-top: 10px;
  border-bottom: $border;
}

.table {
  &-row {
    display: grid;
    grid-template-columns: 20% 1fr 1fr;
    font-size: 1rem;
    line-height: 1.4rem;

    :deep(u:before) {
      opacity: 0.6;
    }

    :deep(.red) {
      color: rgb(218, 38, 80);
      display: block;
      margin-bottom: 10px;
    }

    :deep(.green) {
      color: rgb(69, 190, 174);
      display: block;
      margin-bottom: 10px;
    }

    :deep(ul) {
      margin-left: 15px;
      margin-top: 5px;

      li {
        padding-left: 12px;
        padding-top: 7px;
      }
    }

    h4 {
      font-size: 0.95rem;
      width: 90%;
      line-height: 1.3rem;
      font-weight: 500;
      color: var(--textMedium);
    }
  }

  &-row:nth-of-type(2n) {
    background-color: var(--bgLight);
  }

  &-header {
    position: sticky;
    width: 100%;
    top: calc(var(--navbarHeight));
    left: 0;
    z-index: 10;
    background-color: white;
    border-bottom: $border;
    padding-top: 20px;

    &-cell {
      padding: 30px;
      position: relative;
      border-right: $border;
      border-top: $border;
      background-color: rgb(255, 255, 224);
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      h1 {
        font-size: 1.3rem;
        line-height: 1.9rem;
        margin-bottom: 15px;
        flex: 1;
      }

      :deep(.btn-arrow) {
        padding: 10px 17px 10px 20px;
        font-size: 0.95rem;

        svg {
          margin-left: 7px;
        }
      }
    }

    &-cell:first-of-type {
      border-top: none;
      background-color: transparent;
    }
  }

  &-cell {
    padding: 25px 30px;
    border-right: $border;
    border-top: $border;
  }

  &-title {
    padding: 25px 20px;
    border-left: $border;
  }
}

@media (max-width: 1200px) {
  .table {
    $padding: 20px;

    &-header {
      &-cell {
        width: 50%;
        padding: $padding;

        h1 {
          font-size: 1.1rem;
          line-height: 1.6rem;
        }

        :deep(.btn-arrow) {
          font-size: 0.9rem;
          padding-top: 7px;
          padding-bottom: 7px;
        }
      }

      &-cell:nth-of-type(2n) {
        border-left: $border;
      }

      &-cell:first-of-type {
        display: none;
      }
    }

    &-row {
      display: flex;
      flex-wrap: wrap;
    }

    &-cell {
      width: 50%;
      padding: 35px $padding;
    }

    &-cell:nth-of-type(2n) {
      border-left: $border;
    }

    &-title {
      width: 100%;
      padding: 10px $padding;

      h4 {
        font-size: 0.9rem;
        font-weight: 600;
        color: var(--textLight);
      }
    }
  }
}

@media (max-width: 900px) {
  .table {
    &-header {
      top: 148px;
    }
  }
}

@media (max-width: 600px) {
  .table {
    $padding: 10px;

    &-header {
      top: calc(var(--navbarHeight) + 38px);

      &-cell {
        padding: 20px $padding;

        h1 {
          line-height: 1.4rem;
        }

        :deep(.btn-arrow) {
          width: 100%;
          display: flex;
          align-items: center;
          white-space: pre-wrap;
          border-radius: 10px;
          padding: 9px 12px 9px 10px;
          justify-content: space-between;
          line-height: 1.3rem;
        }
      }
    }

    &-cell {
      padding: 25px $padding;
    }

    &-title {
      padding: 12px $padding;

      h4 {
        font-size: 0.9rem;
      }
    }
  }
}

@media (max-width: 500px) {
  #enrol-table {
  }
}
</style>
